import { memo, useEffect, type ReactElement } from "react";
import { HUD, fullName } from "odyssey-shared";
import { FlagMark, GLYPH_SIZES, Glyph, HudFrame, KIT_METRICS, centreOffset, healthFill, logical } from "../../ui/kit/index.ts";
import { pixelTextWidth } from "../../ui/kit/pixel-text.ts";
import { maskPath, smallDigits, type SmallDigits } from "../../ui/kit/small-digits.ts";
import { HUD_SLOTS, TARGET_CARD, VITALS, framedWidth, resolveSlot, type HudArea, type SlotRect } from "./slots.ts";
import { flagOf, type HudTarget } from "./store.ts";
import { levelText } from "./vitals.ts";
import { say } from "../../words.ts";
import { PageStaffBadge } from "../badge/badge.tsx";
import { badgeLook } from "../badge/look.ts";
import { BADGE_BOX } from "../world-ui/nameplates/layout.ts";

/**
 * The target frame (P2.2-T3, the design's `TargetBox` in `ui_kits/surfaces/CombatHud.jsx`, card
 * `ui_kits/surfaces/cards/target.html`): in `HUD_SLOTS.target`, beside the player card, a HUD frame holding the target's
 * flag mark (a character's), its full name, first and family (P1.9-T24: the family leaves the nameplate for this
 * frame), in its Infamy colour, Neutral for every character until P2.5-T3, a creature's in the danger colour, its level
 * at the right, and its Health percentage as a bar of the danger fill for a target the page reads as attackable and
 * the health fill otherwise, the percentage in the kit's small digits unless the viewer hides health numbers. A
 * training dummy shows its name alone: it has no level, and its Health is never sent. The frame keeps the export's
 * width and widens for a name that leaves the level no room, and starts a gutter right of a player card that a long
 * name widened. A character a Game Master has frozen carries the hourglass after its name, in the info colour, named
 * by the cast bar's words for an act on it. It takes no pointer events. The frame shows only what the snapshot holds;
 * it never predicts.
 */

/**
 * The width a staff badge's visible art takes beside a name, in logical pixels: the 24 visible pixels of its 32 at UI scale 2,
 * 4 and 6 (at 3 and 5 the art is a whole number of device pixels smaller or larger, and the frame's slack takes it up). The
 * badge's transparent room is trimmed away, so the gaps beside it are the flag mark's own `VITALS.nameGap` (P1.9-T122).
 */
const BADGE_ROOM = 12;

/**
 * The width the flag mark takes before the badge or the name: its own 8, or the 12 its lock needs, so the gap after it is
 * the name gap and no more (P1.9-T122).
 *
 * @param flag The target's PvP flag byte.
 * @returns The width in logical pixels.
 */
function markWidth(flag: number): number {
  return flagOf(flag).locked ? KIT_METRICS.flag[VITALS.flagSize].box : VITALS.flagSize;
}

/**
 * The frame as drawn: its content width, the name, whether a frozen character's hourglass follows the name (ADR-056's
 * amendment "a frozen character is safe", decision 45), the level's text (null for none), and the bar (null for a
 * dummy): its fill in whole logical pixels, the percentage's text, the digits (null when the viewer hides health
 * numbers) and their left inside the bar, and its fill's kind.
 */
export interface TargetView {
  readonly width: number;
  readonly name: string;
  readonly held: boolean;
  readonly badge: number;
  readonly level: string | null;
  readonly bar: { readonly fill: number; readonly value: string; readonly digits: SmallDigits | null; readonly valueLeft: number; readonly kind: "danger" | "health" } | null;
}

/**
 * Lays a target out for the frame.
 *
 * @param target The target.
 * @param numbers Whether the viewer shows health numbers.
 * @returns The view.
 */
export function targetView(target: HudTarget, numbers: boolean): TargetView {
  const name = target.kind === "dummy" ? say("hud.target.dummy") : fullName(target.first, target.family);
  const level = levelText(target.level);
  const mark = target.kind === "character" ? markWidth(target.flag) + VITALS.nameGap : 0;
  const held = target.kind === "character" && target.held;
  const look = target.kind === "character" ? badgeLook(target.badge) : null;
  const badge = look === null ? 0 : target.badge;
  const stand = look === null ? 0 : BADGE_ROOM + VITALS.nameGap;
  const width = Math.max(TARGET_CARD.width, mark + stand + pixelTextWidth(name) + (held ? VITALS.nameGap + GLYPH_SIZES.hourglass : 0) + (level === null ? 0 : VITALS.levelGap + pixelTextWidth(level)));
  if (target.kind === "dummy") {
    return { width, name, held, badge, level, bar: null };
  }
  const inner = width - 2 * VITALS.edge;
  const value = `${target.health}%`;
  const digits = numbers ? smallDigits(value) : null;
  return { width, name, held, badge, level, bar: { fill: healthFill(target.health, 100, inner), value, digits, valueLeft: digits === null ? 0 : Math.max(0, centreOffset(inner, digits.width)), kind: target.hostile ? "danger" : "health" } };
}

/**
 * The frame's rectangle on an area as drawn: at the slot's left or a gutter right of the player card, whichever is
 * further right, as wide as the HUD frame round the target's view, at the slot's top and height. What the emote wheel's
 * crown keeps clear of.
 *
 * @param area The area.
 * @param target The target.
 * @param after The player card's right edge, 0 while it is not drawn.
 * @returns The rectangle on the area, whole logical pixels.
 * @example
 * targetFrameRect({ width: 480, height: 270 }, target, cardRight);
 */
export function targetFrameRect(area: HudArea, target: HudTarget, after: number): SlotRect {
  const slot = resolveSlot(HUD_SLOTS.target, area);
  return { left: Math.max(slot.left, after + HUD.gutter), top: slot.top, width: framedWidth(targetView(target, false).width), height: slot.height };
}

/**
 * The frame in its slot.
 *
 * @param props The `area` it is laid out on, the `target`, whether the viewer shows health `numbers`, the right edge
 *   of the player card (`after`), which the frame keeps a gutter from, and `onCommit`.
 * @returns The frame.
 */
export const TargetFrame = memo(function TargetFrame({ area, target, numbers, after, onCommit }: { readonly area: HudArea; readonly target: HudTarget; readonly numbers: boolean; readonly after: number; readonly onCommit: (() => void) | undefined }): ReactElement {
  useEffect(() => {
    onCommit?.();
  });
  const slot = resolveSlot(HUD_SLOTS.target, area);
  const drawn = targetFrameRect(area, target, after);
  const view = targetView(target, numbers);
  const row = KIT_METRICS.flag[VITALS.flagSize];
  const { state, locked } = flagOf(target.flag);
  const bar = view.bar;
  return (
    <div className="ot-hud-slot" data-hud-slot="target" data-handle={target.handle} role="group" aria-label={say("hud.target.frame")} style={{ left: logical(drawn.left), top: logical(drawn.top), minWidth: logical(slot.width) }}>
      <HudFrame>
        <span className="ot-hud-vitals__header" data-hud="target-header" style={{ width: logical(view.width), height: logical(TARGET_CARD.header) }}>
          {target.kind === "character" ? (
            <span className="ot-hud-vitals__flag" data-hud="target-flag" style={{ marginTop: logical(row.markTop), width: logical(markWidth(target.flag)) }}>
              <FlagMark state={state} locked={locked} size={VITALS.flagSize} />
            </span>
          ) : null}
          {view.badge === 0 ? null : (
            <span data-hud="target-badge" style={{ marginLeft: logical(VITALS.nameGap), marginTop: logical(-(BADGE_BOX - TARGET_CARD.header) / 2) }}>
              <PageStaffBadge badge={view.badge} box={BADGE_BOX} tight words />
            </span>
          )}
          <span className={`ot-hud-vitals__name ot-hud-target__name--${target.kind}`} data-hud="target-name" style={{ marginLeft: logical(target.kind === "character" ? VITALS.nameGap : 0), marginTop: logical(row.wordTop) }}>
            {view.name}
          </span>
          {view.held ? (
            <span className="ot-hud-target__held" data-hud="target-held" style={{ marginLeft: logical(VITALS.nameGap), marginTop: logical(row.markTop) }}>
              <Glyph name="hourglass" label={say("bar.refused.targetHeld")} />
            </span>
          ) : null}
          {view.level === null ? null : (
            <span className="ot-hud-vitals__level ot-hud-target__level" data-hud="target-level" style={{ paddingLeft: logical(VITALS.levelGap), marginTop: logical(row.wordTop) }}>
              {view.level}
            </span>
          )}
        </span>
        {bar === null ? null : (
          <span className={`ot-hud-bar ot-hud-bar--${bar.kind}`} data-hud="target-health" role="meter" aria-label={say("hud.target.health")} aria-valuemin={0} aria-valuemax={100} aria-valuenow={target.health} aria-valuetext={bar.value} style={{ marginTop: logical(VITALS.rowGap), width: logical(view.width), height: logical(TARGET_CARD.health) }}>
            <span className="ot-hud-bar__fill" data-hud="target-health-fill" data-fill={bar.fill} style={{ width: logical(bar.fill) }} />
            {bar.digits === null ? null : (
              <svg className="ot-hud-bar__value" data-hud="target-health-value" data-value={bar.value} aria-hidden="true" viewBox={`0 0 ${bar.digits.width} ${bar.digits.height}`} shapeRendering="crispEdges" style={{ left: logical(bar.valueLeft), top: logical(0), width: logical(bar.digits.width), height: logical(bar.digits.height) }}>
                <path className="ot-hud-bar__value-outline" d={maskPath(bar.digits.outline)} />
                <path className="ot-hud-bar__value-glyphs" d={maskPath(bar.digits.glyph)} />
              </svg>
            )}
          </span>
        )}
      </HudFrame>
    </div>
  );
});
